"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import axios from "axios";
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Chip,
  Fade,
} from "@mui/material";
import {
  PictureAsPdf,
  Add,
  Description,
  Slideshow,
  Download,
  MusicNote,
  TableChart,
  Gif,
  DataObject,
  Code,
  Lock,
  Image as ImageIcon,
  Subtitles,
  QrCode2,
  ViewAgenda,
  Link as LinkIcon,
  Key,
  East,
} from "@mui/icons-material";
import { musicProviders } from "@/app/music-links/providers";

type ConverterCategory =
  | "all"
  | "pdf"
  | "video"
  | "data"
  | "code"
  | "image"
  | "music";

interface Converter {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  category: ConverterCategory;
  service: string;
}

type MusicProviderKey = keyof typeof musicProviders;

function MusicLinkConverterSelector() {
  const router = useRouter();
  const [selectedSource, setSelectedSource] = useState<MusicProviderKey | null>(
    null
  );
  const [selectedTarget, setSelectedTarget] = useState<MusicProviderKey | null>(
    null
  );

  const providers = Object.keys(musicProviders) as MusicProviderKey[];

  const handleSourceSelect = (provider: MusicProviderKey) => {
    setSelectedSource(provider);
    setSelectedTarget(null); // Reset target when source changes
  };

  const handleTargetSelect = (provider: MusicProviderKey) => {
    setSelectedTarget(provider);
  };

  const handleConvert = () => {
    if (selectedSource && selectedTarget) {
      router.push(`/music-links/${selectedSource}-to-${selectedTarget}`);
    }
  };

  const availableTargets = providers.filter(
    (p) => p !== selectedSource
  ) as MusicProviderKey[];

  return (
    <Paper
      elevation={8}
      sx={{
        p: 4,
        borderRadius: 4,
        background: "rgba(255, 255, 255, 0.98)",
        border: "2px solid",
        borderColor: "primary.main",
      }}
    >
      <Box sx={{ textAlign: "center", mb: 4 }}>
        <MusicNote sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />
        <Typography
          variant="h4"
          component="h2"
          gutterBottom
          fontWeight={700}
          color="primary.main"
        >
          Music Link Converter
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Convert music links between different streaming platforms. Select your
          source and target platforms below.
        </Typography>
        <Typography variant="body2" sx={{ mb: 4 }}>
          <Link href="/music-links" style={{ fontWeight: 600 }}>
            Browse all music link converters →
          </Link>
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Source Selection */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              fontWeight={600}
              sx={{ mb: 2 }}
            >
              From (Source Platform)
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                justifyContent: "center",
              }}
            >
              {providers.map((provider) => {
                const isSelected = selectedSource === provider;
                return (
                  <Chip
                    key={provider}
                    label={musicProviders[provider].title}
                    onClick={() => handleSourceSelect(provider)}
                    sx={{
                      px: 2,
                      py: 3,
                      fontSize: "0.95rem",
                      fontWeight: isSelected ? 600 : 400,
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      bgcolor: isSelected ? "primary.main" : "grey.100",
                      color: isSelected ? "white" : "text.primary",
                      border: isSelected
                        ? "2px solid"
                        : "2px solid transparent",
                      borderColor: isSelected ? "primary.dark" : "transparent",
                      "&:hover": {
                        bgcolor: isSelected ? "primary.dark" : "grey.200",
                        transform: "translateY(-2px)",
                        boxShadow: isSelected
                          ? "0 4px 12px rgba(102, 126, 234, 0.3)"
                          : "0 2px 8px rgba(0,0,0,0.1)",
                      },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </Grid>

        {/* Arrow */}
        <Grid
          size={{ xs: 12, md: 2 }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Fade in={!!selectedSource}>
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <East
                sx={{
                  fontSize: 48,
                  color: "primary.main",
                  opacity: selectedSource ? 1 : 0.3,
                }}
              />
            </Box>
          </Fade>
        </Grid>

        {/* Target Selection */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              fontWeight={600}
              sx={{ mb: 2 }}
            >
              To (Target Platform)
            </Typography>
            {selectedSource ? (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.5,
                  justifyContent: "center",
                }}
              >
                {availableTargets.map((provider) => {
                  const isSelected = selectedTarget === provider;
                  return (
                    <Chip
                      key={provider}
                      label={musicProviders[provider].title}
                      onClick={() => handleTargetSelect(provider)}
                      sx={{
                        px: 2,
                        py: 3,
                        fontSize: "0.95rem",
                        fontWeight: isSelected ? 600 : 400,
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        bgcolor: isSelected ? "success.main" : "grey.100",
                        color: isSelected ? "white" : "text.primary",
                        border: isSelected
                          ? "2px solid"
                          : "2px solid transparent",
                        borderColor: isSelected
                          ? "success.dark"
                          : "transparent",
                        "&:hover": {
                          bgcolor: isSelected ? "success.dark" : "grey.200",
                          transform: "translateY(-2px)",
                          boxShadow: isSelected
                            ? "0 4px 12px rgba(76, 175, 80, 0.3)"
                            : "0 2px 8px rgba(0,0,0,0.1)",
                        },
                      }}
                    />
                  );
                })}
              </Box>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 4,
                  color: "text.secondary",
                }}
              >
                <Typography variant="body2">
                  Select a source platform first
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Convert Button */}
      {selectedSource && selectedTarget && (
        <Fade in={!!selectedSource && !!selectedTarget}>
          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleConvert}
              startIcon={<LinkIcon />}
              sx={{
                px: 6,
                py: 1.5,
                fontSize: "1.1rem",
                fontWeight: 600,
                background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
                "&:hover": {
                  background:
                    "linear-gradient(45deg, #5568d9 30%, #653a92 90%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 16px rgba(102, 126, 234, 0.3)",
                },
              }}
            >
              Convert {musicProviders[selectedSource].title} to{" "}
              {musicProviders[selectedTarget].title}
            </Button>
          </Box>
        </Fade>
      )}
    </Paper>
  );
}

export default function HomeClient() {
  const [selectedTab, setSelectedTab] = useState<ConverterCategory>("all");
  const [appVersionAndBuild, setAppVersionAndBuild] = useState<string>("");

  useEffect(() => {
    axios
      .get(process.env.NEXT_PUBLIC_URL + "/build.json", {
        timeout: 600000,
      })
      .then((res) => {
        setAppVersionAndBuild(`v${res.data.version}-${res.data.build}`);
      })
      .catch((err) => {
        console.error("Failed to fetch app version and build:", err);
      });
  }, []);

  const converters: Converter[] = [
    {
      id: "image-to-pdf",
      name: "Image to PDF",
      description:
        "Convert JPG, JPEG, and PNG images to PDF format. Support for multiple images.",
      icon: (
        <PictureAsPdf sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />
      ),
      category: "pdf",
      service: "image-to-pdf",
    },
    {
      id: "word-to-pdf",
      name: "Word to PDF",
      description:
        "Convert DOC and DOCX Word documents to PDF format quickly and easily.",
      icon: <Description sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "pdf",
      service: "word-to-pdf",
    },
    {
      id: "excel-to-pdf",
      name: "Excel to PDF",
      description:
        "Convert XLS and XLSX Excel spreadsheets to PDF format quickly and easily.",
      icon: <TableChart sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "pdf",
      service: "excel-to-pdf",
    },
    {
      id: "powerpoint-to-pdf",
      name: "PowerPoint to PDF",
      description:
        "Convert PPT and PPTX PowerPoint presentations to PDF format easily.",
      icon: <Slideshow sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "pdf",
      service: "powerpoint-to-pdf",
    },
    {
      id: "markdown-to-pdf",
      name: "Markdown to PDF",
      description:
        "Convert Markdown files to beautifully formatted PDF documents with syntax highlighting.",
      icon: (
        <PictureAsPdf sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />
      ),
      category: "pdf",
      service: "markdown-to-pdf",
    },
    {
      id: "video-to-audio",
      name: "Video to Audio",
      description:
        "Extract audio from video files. Support for MP3, WAV, AAC, OGG, and FLAC formats.",
      icon: <MusicNote sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "video",
      service: "video-to-audio",
    },
    {
      id: "gif-to-mp4",
      name: "GIF to MP4",
      description:
        "Convert animated GIFs to MP4 video. Smaller file size with adjustable quality.",
      icon: <Gif sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "video",
      service: "gif-to-mp4",
    },
    {
      id: "video-to-gif",
      name: "Video to GIF",
      description:
        "Convert videos to animated GIFs. Customize frame rate, size, and quality.",
      icon: <Gif sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "video",
      service: "video-to-gif",
    },
    {
      id: "x-video-downloader",
      name: "X Video Downloader",
      description:
        "Download videos from public x.com or twitter.com posts as MP4 files.",
      icon: <Download sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "video",
      service: "x-video-downloader",
    },
    {
      id: "srt-to-vtt",
      name: "SRT to VTT",
      description:
        "Convert SRT subtitle files to WebVTT (VTT) for HTML5 video. Fast and free.",
      icon: <Subtitles sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "video",
      service: "srt-to-vtt",
    },
    {
      id: "vtt-to-srt",
      name: "VTT to SRT",
      description:
        "Convert WebVTT (VTT) to SRT (SubRip) for broad compatibility with video tools.",
      icon: <Subtitles sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "video",
      service: "vtt-to-srt",
    },
    {
      id: "json-to-xml",
      name: "JSON to XML",
      description:
        "Convert JSON data to XML format instantly. Upload files or paste JSON directly.",
      icon: <DataObject sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "data",
      service: "json-to-xml",
    },
    {
      id: "xml-to-json",
      name: "XML to JSON",
      description:
        "Convert XML data to JSON format. Fast and accurate parsing with formatted output.",
      icon: <DataObject sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "data",
      service: "xml-to-json",
    },
    {
      id: "markdown-to-html",
      name: "Markdown to HTML",
      description:
        "Convert Markdown files to clean HTML code with live preview.",
      icon: <Code sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "data",
      service: "markdown-to-html",
    },
    {
      id: "html-to-markdown",
      name: "HTML to Markdown",
      description:
        "Convert HTML files to clean Markdown format with live preview.",
      icon: <Code sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "data",
      service: "html-to-markdown",
    },
    {
      id: "html-minifier",
      name: "HTML Minifier",
      description:
        "Minify HTML by removing whitespace and comments. Client-side, fast, and private.",
      icon: <Code sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "code",
      service: "html-minifier",
    },
    {
      id: "css-minifier",
      name: "CSS Minifier",
      description:
        "Minify CSS by removing comments and unnecessary whitespace. Client-side only.",
      icon: <Code sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "code",
      service: "css-minifier",
    },
    {
      id: "js-minifier",
      name: "JavaScript Minifier",
      description:
        "Minify JavaScript code to reduce file size. Fast, private, and runs entirely in your browser.",
      icon: <Code sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "code",
      service: "js-minifier",
    },
    {
      id: "base64-encode-decode",
      name: "Base64 Encode / Decode",
      description:
        "Encode text to Base64 or decode Base64 to text. Fast, secure, and runs entirely in your browser.",
      icon: <Lock sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "data",
      service: "base64-encode-decode",
    },
    {
      id: "url-encoder-decoder",
      name: "URL Encoder & Decoder",
      description:
        "Encode text for URI components (query strings) or decode encoded URI components. Fast, secure, and runs entirely in your browser.",
      icon: <Code sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "data",
      service: "url-encoder-decoder",
    },
    {
      id: "url-parser",
      name: "URL Parser",
      description:
        "Parse URLs into protocol, host, path, query parameters, and fragments. Instant, secure, and client-side only.",
      icon: <LinkIcon sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "data",
      service: "url-parser",
    },
    {
      id: "jwt-parser",
      name: "JWT Parser",
      description:
        "Decode JWT tokens into readable header & payload JSON, inspect claims, and optionally verify HS signatures. Client-side only.",
      icon: <Key sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "data",
      service: "jwt-parser",
    },
    {
      id: "text-to-qrcode",
      name: "Text to QR Code",
      description:
        "Convert text, URLs, or any string to QR code. Customize size, colors, and format. Client-side, fast, and private.",
      icon: <QrCode2 sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "data",
      service: "text-to-qrcode",
    },
    {
      id: "text-to-barcode",
      name: "Text to Barcode",
      description:
        "Convert text or numbers to 1D barcode. CODE128, EAN-13, UPC, CODE39, and 15+ formats. Download as PNG, SVG, JPEG, or WebP. Client-side only.",
      icon: <ViewAgenda sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "data",
      service: "text-to-barcode",
    },
    {
      id: "image-converters",
      name: "Image Converters",
      description:
        "Convert between image formats: JPG, PNG, WebP, SVG, and more. Browse all image converters.",
      icon: <ImageIcon sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "image",
      service: "image-converters",
    },
    {
      id: "jpg-to-webp",
      name: "JPG to WebP",
      description:
        "Convert JPG/JPEG images to WebP format. Smaller file sizes with better quality.",
      icon: <ImageIcon sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "image",
      service: "jpg-to-webp",
    },
    {
      id: "png-to-webp",
      name: "PNG to WebP",
      description:
        "Convert PNG images to WebP format. Reduce file sizes while preserving transparency.",
      icon: <ImageIcon sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "image",
      service: "png-to-webp",
    },
    {
      id: "svg-to-png",
      name: "SVG to PNG",
      description:
        "Convert SVG vector images to PNG raster format. Customizable output size.",
      icon: <ImageIcon sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "image",
      service: "svg-to-png",
    },
    {
      id: "webp-to-jpg",
      name: "WebP to JPG",
      description:
        "Convert WebP images to JPG format for maximum compatibility.",
      icon: <ImageIcon sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "image",
      service: "webp-to-jpg",
    },
    {
      id: "webp-to-png",
      name: "WebP to PNG",
      description:
        "Convert WebP images to PNG format. Preserve transparency and quality.",
      icon: <ImageIcon sx={{ fontSize: 80, color: "primary.main", mb: 2 }} />,
      category: "image",
      service: "webp-to-png",
    },
  ];

  const filteredConverters =
    selectedTab === "all"
      ? converters
      : converters.filter((converter) => converter.category === selectedTab);

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: ConverterCategory
  ) => {
    setSelectedTab(newValue);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          py: 12,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          },
        }}
      >
        <Container
          maxWidth="lg"
          sx={{ position: "relative", zIndex: 1, textAlign: "center" }}
        >
          <Image
            src="/logo.png"
            alt="File Converter Logo"
            width={170}
            height={135}
            priority
            style={{ filter: "brightness(0) invert(1)", marginBottom: "2rem" }}
          />

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2.5rem", md: "4rem" },
              fontWeight: 800,
              mb: 3,
              textShadow: "0 2px 10px rgba(0,0,0,0.2)",
            }}
          >
            Free Online File Converter
          </Typography>

          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: "1.1rem", md: "1.5rem" },
              fontWeight: 400,
              mb: 5,
              maxWidth: "800px",
              mx: "auto",
              opacity: 0.95,
            }}
          >
            Convert your files instantly with our free, secure, and easy-to-use
            tools. No registration required, everything happens in your browser.
          </Typography>
        </Container>
      </Box>

      {/* Available Converters */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h2"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 700,
            fontSize: { xs: "2rem", md: "2.5rem" },
            mb: 4,
            color: "primary.main",
          }}
        >
          Available Converters
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
                minHeight: 48,
              },
            }}
          >
            <Tab label="All" value="all" />
            <Tab label="PDF Converters" value="pdf" />
            <Tab label="Video & Audio" value="video" />
            <Tab label="Data Formats" value="data" />
            <Tab label="Code Minifiers" value="code" />
            <Tab label="Image Converters" value="image" />
            <Tab label="Music Links" value="music" />
          </Tabs>
        </Box>

        {selectedTab === "code" && (
          <>
            <Typography
              variant="h3"
              align="center"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.6rem", md: "2rem" },
                mb: 1,
                color: "primary.main",
              }}
            >
              Code Minifiers
            </Typography>
            <Typography
              variant="body1"
              align="center"
              color="text.secondary"
              sx={{ mb: 5, fontSize: "1.05rem", maxWidth: 900, mx: "auto" }}
            >
              Make your code smaller for faster websites and apps. All tools run
              client-side, so your code stays on your device.
            </Typography>
          </>
        )}

        {/* Music Link Converter - Special UI */}
        {selectedTab === "music" ? (
          <MusicLinkConverterSelector />
        ) : (
          <Grid container spacing={4}>
            {filteredConverters.map((converter) => (
              <Grid key={converter.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    border: "2px solid",
                    borderColor: "divider",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: "primary.main",
                      transform: "translateY(-8px)",
                      boxShadow: "0 12px 24px rgba(102, 126, 234, 0.15)",
                    },
                  }}
                >
                  <Link
                    href={converter.service}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <CardContent sx={{ textAlign: "center", py: 5, px: 3 }}>
                      {converter.icon}
                      <Typography
                        variant="h5"
                        component="h3"
                        gutterBottom
                        fontWeight={600}
                      >
                        {converter.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                      >
                        {converter.description}
                      </Typography>
                      <Button
                        variant="contained"
                        fullWidth
                        sx={{
                          background:
                            "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
                        }}
                      >
                        Convert Now
                      </Button>
                    </CardContent>
                  </Link>
                </Card>
              </Grid>
            ))}

            {/* More Converters Coming Soon */}
            {selectedTab === "all" && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    border: "2px dashed",
                    borderColor: "divider",
                    bgcolor: "grey.50",
                  }}
                >
                  <CardContent sx={{ textAlign: "center", py: 5, px: 3 }}>
                    <Add sx={{ fontSize: 80, color: "grey.400", mb: 2 }} />
                    <Typography
                      variant="h5"
                      component="h3"
                      gutterBottom
                      fontWeight={600}
                      color="text.secondary"
                    >
                      More Converters
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      More file converters coming soon. Stay tuned!
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        )}
      </Container>

      {/* Features Section */}
      <Box sx={{ bgcolor: "grey.50", py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: "2rem", md: "2.5rem" },
              mb: 6,
              color: "primary.main",
            }}
          >
            Why Choose Our Converter?
          </Typography>

          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: "center",
                  height: "100%",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  🔒 100% Secure
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All conversions happen in your browser. Your files never leave
                  your device.
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: "center",
                  height: "100%",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  ⚡ Lightning Fast
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Instant conversion with no waiting. Process multiple files at
                  once.
                </Typography>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: "center",
                  height: "100%",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  💯 Free Forever
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No hidden fees, no subscriptions. Completely free to use,
                  unlimited conversions.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 6, textAlign: "center", bgcolor: "background.paper" }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            © 2026 Free Online File Converter. All rights reserved.{" "}
            {appVersionAndBuild}
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
